import React from 'react';
import { Badge, Dropdown } from 'react-bootstrap';
import { LiveStockInfo } from './LiveStockInfo';
import { OrderItemAction } from '../../api/dto/OrderItemAction';
import If from '../../component/If';

interface Props {
  item: any;
  order: any;
  liveStockInfo: any;
  onAction: (actionType: any, orderId: number, orderItemId: number, qty: number | null) => void;
  onShowPartialModal: (orderId: number, orderItemId: number, item: any) => void;
}

const CustomToggle = React.forwardRef(({ children, onClick }: any, ref: any) => (
  <a
    href=""
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    style={{
      textDecoration: "none",
      color: "#6c757d",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "8px",
      borderRadius: "4px",
      transition: "all 0.2s",
      cursor: "pointer"
    }}
    onMouseEnter={(e) => e.currentTarget.style.color = "#212529"}
    onMouseLeave={(e) => e.currentTarget.style.color = "#6c757d"}
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
      <path d="M9.405 1.05c-.413-1.4-2.397-1.4-2.81 0l-.1.34a1.464 1.464 0 0 1-2.105.872l-.31-.17c-1.283-.698-2.686.705-1.987 1.987l.169.311c.446.82.023 1.841-.872 2.105l-.34.1c-1.4.413-1.4 2.397 0 2.81l.34.1a1.464 1.464 0 0 1 .872 2.105l-.17.31c-.698 1.283.705 2.686 1.987 1.987l.311-.169a1.464 1.464 0 0 1 2.105.872l.1.34c.413 1.4 2.397 1.4 2.81 0l.1-.34a1.464 1.464 0 0 1 2.105-.872l.31.17c1.283.698 2.686-.705 1.987-1.987l-.169-.311a1.464 1.464 0 0 1 .872-2.105l.34-.1c1.4-.413 1.4-2.397 0-2.81l-.34-.1a1.464 1.464 0 0 1-.872-2.105l.17-.31c.698-1.283-.705-2.686-1.987-1.987l-.311.169a1.464 1.464 0 0 1-2.105-.872l-.1-.34zM8 10.93a2.929 2.929 0 1 1 0-5.86 2.929 2.929 0 0 1 0 5.858z"/>
    </svg>
    {children}
  </a>
));

export const OrderItemRow: React.FC<Props> = ({ item, order, liveStockInfo, onAction, onShowPartialModal }) => {

  const status = item.statusDescription?.name;
  const isNotCompleted = order && (order.statusDescription?.name !== "COMPLETED" && order.statusDescription?.name !== "CANCELED_BY_SELLER");

  const formatCurrency = (value: number) => {
    return value ? value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00";
  }

  // Aksiyon Görünürlük Mantığı
  const showApprove = isNotCompleted && ("WAITING_FOR_APPROVAL" === status);
  const showPartial = isNotCompleted && ("WAITING_FOR_APPROVAL" === status) && item.quantity > 0;
  const showCancel = isNotCompleted && ("WAITING_FOR_APPROVAL" === status);
  const showTakeBack = isNotCompleted && (["APPROVED", "APPROVED_PARTIALLY", "WAITING_FOR_WAREHOUSE_OPERATION"].includes(status));
  const hasAnyAction = showApprove || showCancel || showPartial || showTakeBack;

  return (
    <div className="row mx-0" style={{
      borderBottom: "1px solid #f0f0f0",
      padding: "16px 0",
      alignItems: "center",
      backgroundColor: "#fff"
    }}>

      {/* KOLON 1: Ürün Bilgisi (Sola Yaslı) */}
      <div className="col-md-5">
        <div style={{ fontSize: "0.95rem", fontWeight: "600", color: "#2c3e50", marginBottom: "4px", textTransform: "uppercase" }}>
            {item.productName}
        </div>
        <div style={{ fontSize: "0.8rem", color: "#888", display: "flex", gap: "10px", alignItems: "center" }}>
           <span style={{ fontFamily: "monospace", letterSpacing: "0.5px" }}>{item.stockCode}</span>
           <span style={{width: "4px", height: "4px", backgroundColor: "#ccc", borderRadius: "50%"}}></span>
           <span style={{
             backgroundColor: "#f8f9fa",
             padding: "2px 8px",
             borderRadius: "4px",
             border: "1px solid #eee",
             color: "#555",
             fontWeight: "500"
           }}>
              {formatCurrency(item.perPriceOnTransaction)} ₺
           </span>
        </div>
      </div>

      {/* KOLON 2: Sipariş Adedi (Merkez) */}
      <div className="col-md-2 text-center" style={{ borderLeft: "1px solid #f8f8f8", borderRight: "1px solid #f8f8f8" }}>
        <div style={{ fontSize: "0.65rem", color: "#bbb", textTransform: "uppercase", fontWeight: "700", marginBottom: "2px" }}>Miktar</div>
        <div style={{ fontSize: "1.7rem", fontWeight: "800", color: "#0d6efd", lineHeight: "1" }}>
            {item.quantity}
        </div>
      </div>

      {/* KOLON 3: Stok Bilgisi (Merkez) */}
      <div className="col-md-3 d-flex justify-content-center">
         <LiveStockInfo liveStockInfo={liveStockInfo} />
      </div>

      {/* KOLON 4: Durum ve İşlem (Sağa Yaslı) */}
      <div className="col-md-2 d-flex flex-column align-items-end justify-content-center" style={{ gap: "6px" }}>

        <Badge
          bg={
            status === 'APPROVED' ? 'success' :
            status === 'CANCELED' ? 'danger' :
            status === 'REJECTED' ? 'danger' : 'secondary'
          }
          style={{
            fontSize: "0.7rem",
            fontWeight: "600",
            padding: "6px 10px",
            minWidth: "90px",
            textAlign: "center",
            textTransform: "uppercase"
          }}
        >
          {item.statusDescription?.label || status}
        </Badge>

        <If value={hasAnyAction}>
          <Dropdown align="end">
            <Dropdown.Toggle as={CustomToggle} id={`dropdown-actions-${item.id}`} />

            <Dropdown.Menu style={{ fontSize: "0.85rem", boxShadow: "0 10px 25px rgba(0,0,0,0.1)", border: "1px solid #eee" }}>
              <If value={showApprove}>
                <Dropdown.Item onClick={() => onAction("approve", order.id, item.id, null)} style={{ fontWeight: "600", color: "#198754" }}>
                  <span style={{ marginRight: "10px" }}>✅</span> Onayla
                </Dropdown.Item>
              </If>

              <If value={showPartial}>
                <Dropdown.Item onClick={() => onShowPartialModal(order.id, item.id, item)} style={{ color: "#0d6efd" }}>
                  <span style={{ marginRight: "10px" }}>✂️</span> Parçalı Onayla
                </Dropdown.Item>
              </If>

              {(showApprove || showPartial) && <Dropdown.Divider />}

              <If value={showCancel}>
                <Dropdown.Item onClick={() => onAction("cancel", order.id, item.id, null)} style={{ color: "#dc3545" }}>
                  <span style={{ marginRight: "10px" }}>⛔</span> İptal Et (Reddet)
                </Dropdown.Item>
              </If>

              <If value={showTakeBack}>
                <Dropdown.Item onClick={() => onAction(OrderItemAction.TAKE_BACK_FROM_WAREHOUSE, order.id, item.id, null)} style={{ color: "#fd7e14" }}>
                  <span style={{ marginRight: "10px" }}>↩️</span> Geri Al (Taslağa Çek)
                </Dropdown.Item>
              </If>
            </Dropdown.Menu>
          </Dropdown>
        </If>
      </div>
    </div>
  );
};